import { Injectable } from '@angular/core';
import { TextEditorHistoryService } from '@fi-shared/fi-text-editor/fi-text-editor-helper-services/text-editor-history.service';
import { findClosest, isDefined } from '@fi-shared/utils';
import { FiTextItemHelperService } from '@fi-shared/fi-text-item-helper/fi-text-item-helper.service';
import {
    FiTextEditorInsertEmbedCodeDialogComponent,
    ITextEditorInsertEmbedCodeDialogData,
} from '@fi-shared/text-editor/text-editor-insert-embed-code-dialog/text-editor-insert-embed-code-dialog.component';
import { OperationOverlaySmallService } from '@fi-shared/operation-overlay-small/operation-overlay-small.service';
import { AdvancedTextPanelMediaLinkService } from '@fi-shared/fi-text-panel/text-panel-services/advanced-text-panel-media-link.service';

interface IInsertEmbedParams {
    model?: ITextEditorInsertEmbedCodeDialogData;
    cssClass?: string;
}

@Injectable()
export class TextEditorInsertEmbedCodeService {
    private readonly textItemEmbedTagName: string = 'ccm-text-item-embed';
    private readonly embedCodeOperationOverlaySmallClassName: string = 'insert-embed-code-operation-overlay-small';
    private readonly textEditorSelectorClassName: string = '.text-editor';
    private readonly contenteditableAttrName: string = 'contenteditable';
    private readonly contenteditableAttrSelector: string = `[${this.contenteditableAttrName}]`;
    private readonly textItemModelAttrName: string = 'text-item-model';
    private readonly styleAttrName: string = 'style';

    constructor(
        private textEditorHistoryService: TextEditorHistoryService,
        private operationOverlaySmallService: OperationOverlaySmallService,
        private fiTextItemHelperService: FiTextItemHelperService,
    ) {}

    private _isDialogOpened: boolean = false;
    get isDialogOpened(): boolean {
        return this._isDialogOpened;
    }

    insertEmbedCode(targetElement: HTMLElement, params?: IInsertEmbedParams) {
        params = this.getDialogParams(params);
        const selection: Selection = window.getSelection();
        const range: Range = this.getSelectionRange(selection, targetElement);
        this._isDialogOpened = true;
        this.operationOverlaySmallService
            .openOperationOverlaySmallDialog(FiTextEditorInsertEmbedCodeDialogComponent, params)
            .afterClosed()
            .subscribe((embedModel: ITextEditorInsertEmbedCodeDialogData) => {
                this._isDialogOpened = false;
                if (!isDefined(embedModel)) return;

                this.textEditorHistoryService.saveState();

                // remove embed element in case of editing and new text is empty
                if (!embedModel.EmbedText) {
                    if (!this.isEmbedElement(targetElement)) {
                        return;
                    }
                    targetElement.remove();
                }

                this.insertEmbedElement(targetElement, this.createNewEmbedElement(embedModel), range);
                setTimeout(() => {
                    this.fiTextItemHelperService.executeScripts(targetElement);
                });
                selection.removeAllRanges();
                selection.addRange(range);
            });
    }

    private getDialogParams(params: IInsertEmbedParams): IInsertEmbedParams {
        params = params || {};
        params.cssClass = this.embedCodeOperationOverlaySmallClassName;
        return params;
    }

    private isEmbedElement(targetElement: HTMLElement): boolean {
        return targetElement.tagName.toLowerCase() === this.textItemEmbedTagName;
    }

    private insertEmbedElement(targetElement: HTMLElement, newEmbedElement: HTMLElement, range: Range) {
        if (this.isEmbedElement(targetElement)) {
            targetElement.replaceWith(newEmbedElement);
        } else {
            this.insertNewEmbedElement(range, newEmbedElement);
        }
        this.fiTextItemHelperService.updateIFrameSourcesAndStylesOfEmbedElement(newEmbedElement);
    }

    private setStylesAndContent(targetElement: HTMLElement, embedModel: ITextEditorInsertEmbedCodeDialogData) {
        targetElement.innerHTML = embedModel.EmbedText;

        const iframeSizeStyles: string = this.fiTextItemHelperService.getIframeSizeStyles(embedModel.EmbedText);
        if (!!iframeSizeStyles.length) {
            (targetElement.firstChild as HTMLElement).setAttribute(this.styleAttrName, iframeSizeStyles);
        }
    }

    private getContenteditableElement(element: HTMLElement): HTMLElement {
        return element.matches(this.contenteditableAttrSelector)
            ? element
            : element.querySelector(this.contenteditableAttrSelector);
    }

    private getSelectionRange(selection: Selection, element: HTMLElement): Range {
        let range = selection.rangeCount ? selection.getRangeAt(0) : undefined;
        if (this.isEmptyRange(range)) {
            range = new Range();
            const contentEditableElement: HTMLElement = this.getContenteditableElement(element);

            if (isDefined(contentEditableElement)) {
                range.selectNodeContents(contentEditableElement);
                range.collapse(false);
            }
        }
        return range;
    }

    private isEmptyRange(range: Range): boolean {
        return !range || this.isRangeContainsTextEditorElement(range);
    }

    private isRangeContainsTextEditorElement(range: Range): boolean {
        const startContainer = range.startContainer;
        return !isDefined(findClosest(startContainer, this.textEditorSelectorClassName));
    }

    private createNewEmbedElement(embedModel: ITextEditorInsertEmbedCodeDialogData): HTMLElement {
        const newEmbedElement: HTMLElement = document.createElement(this.textItemEmbedTagName);
        newEmbedElement.setAttribute(this.contenteditableAttrName, 'false');
        newEmbedElement.setAttribute(this.textItemModelAttrName, JSON.stringify(embedModel));
        setTimeout(() => {
            this.setStylesAndContent(newEmbedElement, embedModel);
        });
        return newEmbedElement;
    }

    private insertNewEmbedElement(range: Range, newEmbedElement: HTMLElement) {
        range.deleteContents();
        const spanElement: HTMLSpanElement = document.createElement('span');
        spanElement.innerHTML = '&nbsp;';
        const embedContainerElement: HTMLDivElement = document.createElement('div');
        embedContainerElement.appendChild(spanElement.cloneNode(true));
        embedContainerElement.appendChild(newEmbedElement);
        embedContainerElement.appendChild(spanElement);

        range.insertNode(embedContainerElement);
        range.collapse(false);
    }
}
